import React, { useState, useEffect } from 'react';
import {
  FaShieldAlt,
  FaBolt,
  FaHeart,
  FaStar,
  FaSkull,
  FaArrowRight,
  FaCrosshairs,
  FaPlusCircle,
} from 'react-icons/fa';

// Custom empty circle component for Shield ability
const EmptyCircle = ({ size = 18, style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <circle cx="12" cy="12" r="10" />
  </svg>
);

/**
 * Animated ability symbol that fades in, flies from bottom, then disappears
 * Used to show when abilities are gained or when units with abilities appear
 */
export const AbilitySymbolAnimation = ({ ability, onComplete, delay = 0, position = 'center' }) => {
  const isCenterLarge = position === 'centerLarge';
  const [isVisible, setIsVisible] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    // Start animation after delay
    const timer = setTimeout(() => {
      setIsVisible(true);
      
      // Auto-remove after animation completes
      const removeTimer = setTimeout(() => {
        setIsVisible(false);
        setHasCompleted(true);
        if (onComplete) {
          // Call onComplete immediately, don't wait for fade out
          onComplete();
        }
      }, 1500); // Show for 1.5 seconds
      
      return () => clearTimeout(removeTimer);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, onComplete]);

  // Get ability icon component
  const getAbilityIcon = (abilityName) => {
    const iconMap = {
      'Protect': FaShieldAlt,
      'Shield': EmptyCircle,
      'Blitz': FaBolt,
      'Recover': FaHeart,
      'Redeem': FaStar,
      'Fell': FaSkull,
      'Penetrate': FaArrowRight,
      'Sniper': FaCrosshairs,
      'MendWounds': FaPlusCircle,
    };
    return iconMap[abilityName] || null;
  };

  // Get the icon component for this ability
  const IconComponent = getAbilityIcon(ability);
  
  // Don't render at all if ability is missing, icon is null, animation completed, or not yet visible
  if (!ability || !IconComponent || hasCompleted || !isVisible) return null;

  // Position styles based on position prop
  const getPositionStyles = () => {
    if (position === 'topRight') {
      return {
        top: '-12px',
        right: '32px',
        transform: 'none',
      };
    }
    if (position === 'centerLarge') {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }
    // Default center-bottom position
    return {
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
    };
  };

  const positionStyles = getPositionStyles();

  // Animation keyframes based on position
  const getAnimationKeyframes = () => {
    if (position === 'topRight') {
      return `
        @keyframes abilityPulseTopRight {
          0% {
            transform: translateY(60px) scale(0.8);
            opacity: 0;
          }
          20% {
            transform: translateY(0px) scale(1.2);
            opacity: 1;
          }
          80% {
            transform: translateY(0px) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateY(-5px) scale(0.8);
            opacity: 0;
          }
        }
      `;
    }
    if (position === 'centerLarge') {
      return `
        @keyframes abilityCenterLargeBurst {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5);
          }
          18% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.08);
          }
          28% {
            transform: translate(-50%, -50%) scale(1);
          }
          78% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.92);
          }
        }
      `;
    }
    // Default center animation
    return `
      @keyframes abilityPulse {
        0% {
          transform: translateX(-50%) translateY(30px) scale(0.8);
          opacity: 0;
        }
        20% {
          transform: translateX(-50%) translateY(0px) scale(1.2);
          opacity: 1;
        }
        80% {
          transform: translateX(-50%) translateY(0px) scale(1);
          opacity: 1;
        }
        100% {
          transform: translateX(-50%) translateY(-20px) scale(0.8);
          opacity: 0;
        }
      }
    `;
  };

  const animationName =
    position === 'topRight'
      ? 'abilityPulseTopRight'
      : position === 'centerLarge'
        ? 'abilityCenterLargeBurst'
        : 'abilityPulse';

  const iconSize = isCenterLarge ? 88 : 24;

  return (
    <>
      <div
        style={{
          position: 'absolute',
          ...positionStyles,
          zIndex: isCenterLarge ? 25 : 15,
          pointerEvents: 'none',
          color: 'white',
          filter: isCenterLarge
            ? 'drop-shadow(0 0 18px rgba(255, 220, 120, 0.85)) drop-shadow(2px 4px 8px rgba(0,0,0,0.75))'
            : 'drop-shadow(2px 2px 4px rgba(0,0,0,0.8))',
          animation: `${animationName} 1.5s ease-in-out`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconComponent size={iconSize} />
      </div>
      <style>{getAnimationKeyframes()}</style>
    </>
  );
};

/**
 * Hook to track ability changes and trigger animations
 */
export const useAbilityAnimations = (order, minimal, zoneName, options = {}) => {
  const { largeAbilityRevealUntil = 0 } = options;
  const [animatingAbilities, setAnimatingAbilities] = useState([]);
  const previousAbilitiesRef = React.useRef(new Map()); // Track abilities per instanceId
  const previousLocationRef = React.useRef(new Map()); // Track location per instanceId
  const hasAppearedRef = React.useRef(new Map()); // Track appearance per instanceId
  const previousInstanceIdRef = React.useRef(null);
  const previousMinimalRef = React.useRef(null);
  const lastAnimationTriggerRef = React.useRef(new Map()); // Track last animation trigger to prevent duplicates
  const currentlyAnimatingRef = React.useRef(new Set()); // Track which ability+instanceId combinations are currently animating

  useEffect(() => {
    // Only process when minimal is true (don't reset on hover/unhover)
    if (!order || !minimal) {
      previousMinimalRef.current = minimal;
      return;
    }

    // Don't trigger animations just because minimal changed from false to true (hover/unhover)
    // Only process if this is the first time seeing minimal=true, or if order/abilities/location actually changed
    const wasMinimalFalse = previousMinimalRef.current === false;
    previousMinimalRef.current = minimal;

    const currentInstanceId = order.instanceId || order.id;
    
    // Reset tracking if this is a different unit
    if (previousInstanceIdRef.current && previousInstanceIdRef.current !== currentInstanceId) {
      // Clear animations for previous unit
      setAnimatingAbilities([]);
      // Clear currently animating set for previous unit
      const previousInstanceId = previousInstanceIdRef.current;
      currentlyAnimatingRef.current.forEach(key => {
        if (key.startsWith(`${previousInstanceId}-`)) {
          currentlyAnimatingRef.current.delete(key);
        }
      });
    }
    previousInstanceIdRef.current = currentInstanceId;

    const currentAbilities = getAbilities(order);
    // Filter out abilities that don't have icons (Immobilize, Confuse, etc.)
    const abilitiesWithSymbols = currentAbilities.filter(ability => {
      const iconMap = {
        'Protect': true,
        'Shield': true,
        'Blitz': true,
        'Recover': true,
        'Redeem': true,
        'Fell': true,
        'Penetrate': true,
        'Sniper': true,
        'MendWounds': true,
      };
      return iconMap[ability] === true;
    });
    
    // Determine location: zoneName prop indicates battlefield, null/undefined indicates hand
    const currentLocation = zoneName || order.zone || 'hand';

    // Get previous state for this instanceId (also filter to only abilities with icons)
    const previousAbilities = (previousAbilitiesRef.current.get(currentInstanceId) || []).filter(ability => {
      const iconMap = {
        'Protect': true, 'Shield': true, 'Blitz': true, 'Recover': true, 'Redeem': true,
        'Fell': true, 'Penetrate': true, 'Sniper': true, 'MendWounds': true,
      };
      return iconMap[ability] === true;
    });
    const previousLocation = previousLocationRef.current.get(currentInstanceId);
    const hasAppeared = hasAppearedRef.current.get(currentInstanceId) || false;

    // Check if location actually changed (not just minimal prop changing)
    const locationChanged = previousLocation !== undefined && previousLocation !== currentLocation;
    const isFirstAppearance = previousLocation === undefined && abilitiesWithSymbols.length > 0;
    
    // Check if abilities were gained (new abilities not in previous list)
    const newAbilities = abilitiesWithSymbols.filter(
      ability => !previousAbilities.includes(ability)
    );

    // Skip if we're just coming back from hover (minimal was false, now true) and nothing actually changed
    if (wasMinimalFalse && previousLocation === currentLocation && 
        JSON.stringify(previousAbilities.sort()) === JSON.stringify(abilitiesWithSymbols.sort())) {
      // Just update refs without triggering animations
      previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
      previousLocationRef.current.set(currentInstanceId, currentLocation);
      return;
    }

    // Create a unique trigger key to prevent duplicate animations
    const sortedAbilities = [...abilitiesWithSymbols].sort(); // Sort copy for consistent comparison
    const sortedPreviousAbilities = [...previousAbilities].sort();
    const abilitiesKey = JSON.stringify(sortedAbilities);
    const triggerKey = `${currentInstanceId}-${currentLocation}-${abilitiesKey}`;
    const lastTrigger = lastAnimationTriggerRef.current.get(triggerKey);
    const timeSinceLastTrigger = lastTrigger ? Date.now() - lastTrigger : Infinity;
    
    // Check if we've already processed this exact state (immediate check)
    // Only consider it "already processed" if:
    // - Location hasn't changed AND
    // - Abilities haven't changed AND  
    // - We've already appeared (not first appearance)
    const alreadyProcessed = hasAppeared && 
                            previousLocation === currentLocation && 
                            JSON.stringify(sortedPreviousAbilities) === abilitiesKey;
    
    // Only trigger if:
    // 1. We haven't triggered for this exact state in the last 500ms (prevents double triggers from React Strict Mode)
    // 2. We haven't already processed this exact state (prevents duplicate triggers in same render)
    const shouldTrigger = timeSinceLastTrigger > 500 && !alreadyProcessed;

    // Helper to check if an ability is currently animating
    const isCurrentlyAnimating = (abilityName) => {
      const animatingKey = `${currentInstanceId}-${abilityName}`;
      return currentlyAnimatingRef.current.has(animatingKey);
    };

    // Helper to mark ability as animating
    const markAsAnimating = (abilityName) => {
      const animatingKey = `${currentInstanceId}-${abilityName}`;
      currentlyAnimatingRef.current.add(animatingKey);
    };

    // Trigger animations for:
    // 1. Unit first appears with abilities (first time we see it, regardless of location)
    // 2. Unit moves to a new location with abilities (hand -> zone or zone -> zone)
    // 3. Abilities were gained during play (only if unit has already appeared)
    if (isFirstAppearance && shouldTrigger) {
      // Unit first appeared - show all abilities
      // ✅ CRITICAL: Update refs IMMEDIATELY to prevent duplicate triggers
      hasAppearedRef.current.set(currentInstanceId, true);
      previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
      previousLocationRef.current.set(currentInstanceId, currentLocation);
      lastAnimationTriggerRef.current.set(triggerKey, Date.now());
      
      // Only add abilities that aren't currently animating
      const abilitiesToShow = abilitiesWithSymbols
        .filter(ability => !isCurrentlyAnimating(ability))
        .map((ability, index) => {
          markAsAnimating(ability);
          return {
            ability,
            instanceId: currentInstanceId,
            id: `${currentInstanceId}-${ability}-${Date.now()}-${index}`,
            delay: index * 200, // Stagger animations
          };
        });
      
      if (abilitiesToShow.length > 0) {
        setAnimatingAbilities(abilitiesToShow);
      }
    } else if (locationChanged && abilitiesWithSymbols.length > 0 && hasAppeared && shouldTrigger) {
      // Unit moved to new location - show all abilities again
      // ✅ CRITICAL: Update refs IMMEDIATELY to prevent duplicate triggers
      previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
      previousLocationRef.current.set(currentInstanceId, currentLocation);
      lastAnimationTriggerRef.current.set(triggerKey, Date.now());
      
      // Only add abilities that aren't currently animating
      const abilitiesToShow = abilitiesWithSymbols
        .filter(ability => !isCurrentlyAnimating(ability))
        .map((ability, index) => {
          markAsAnimating(ability);
          return {
            ability,
            instanceId: currentInstanceId,
            id: `${currentInstanceId}-${ability}-${Date.now()}-${index}`,
            delay: index * 200,
          };
        });
      
      if (abilitiesToShow.length > 0) {
        setAnimatingAbilities(abilitiesToShow);
      }
    } else if (newAbilities.length > 0 && hasAppeared && shouldTrigger) {
      // Abilities gained during play - show only new ones (only if unit has already appeared)
      // Create trigger key for new abilities
      const newAbilitiesKey = `${currentInstanceId}-${currentLocation}-new-${JSON.stringify(newAbilities)}`;
      const lastNewAbilitiesTrigger = lastAnimationTriggerRef.current.get(newAbilitiesKey);
      const timeSinceNewAbilities = lastNewAbilitiesTrigger ? Date.now() - lastNewAbilitiesTrigger : Infinity;
      
      if (timeSinceNewAbilities > 500) {
        // ✅ CRITICAL: Update refs IMMEDIATELY to prevent duplicate triggers
        previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
        previousLocationRef.current.set(currentInstanceId, currentLocation);
        lastAnimationTriggerRef.current.set(newAbilitiesKey, Date.now());
        
        // Only add abilities that aren't currently animating
        const useLargeCenterReveal =
          typeof largeAbilityRevealUntil === 'number' &&
          largeAbilityRevealUntil > Date.now();

        const abilitiesToShow = newAbilities
          .filter(ability => !isCurrentlyAnimating(ability))
          .map((ability, index) => {
            markAsAnimating(ability);
            return {
              ability,
              instanceId: currentInstanceId,
              id: `${currentInstanceId}-${ability}-${Date.now()}-${index}`,
              delay: index * 200,
              presentation: useLargeCenterReveal ? 'large' : undefined,
            };
          });
        
        if (abilitiesToShow.length > 0) {
          setAnimatingAbilities(prev => [...prev, ...abilitiesToShow]);
        }
      }
    } else {
      // No animation triggered - still update refs to track state
      previousAbilitiesRef.current.set(currentInstanceId, abilitiesWithSymbols);
      previousLocationRef.current.set(currentInstanceId, currentLocation);
    }
  }, [order, minimal, zoneName, largeAbilityRevealUntil, order?.abilities, order?.zone, order?.instanceId, order?.id]);

  const removeAnimation = (id) => {
    setAnimatingAbilities(prev => {
      // Find the animation being removed to get its ability name and instanceId
      const animToRemove = prev.find(anim => anim.id === id);
      if (animToRemove) {
        // Remove from currently animating set
        const animatingKey = `${animToRemove.instanceId || 'unknown'}-${animToRemove.ability}`;
        currentlyAnimatingRef.current.delete(animatingKey);
      }
      return prev.filter(anim => anim.id !== id);
    });
  };

  return { animatingAbilities, removeAnimation };
};

// Helper function to get abilities (same as in OrderDesign)
function getAbilities(order) {
  if (!order) return [];
  // ✅ FIX: Check instanceAbilities first if hasInstanceModifications is true
  // This ensures dynamically added abilities (e.g., Blitz from Exolot Finisher) are included
  if (order.hasInstanceModifications && Array.isArray(order.instanceAbilities)) {
    return order.instanceAbilities;
  }
  if (Array.isArray(order.abilities)) return order.abilities;
  if (typeof order.ability === "string") return [order.ability];
  if (Array.isArray(order.abilitiesList)) return order.abilitiesList;
  return [];
}

export default AbilitySymbolAnimation;

